import React, { useState } from 'react';
import { 
  Layout, 
  Menu, 
  Button, 
  Tooltip,
  Drawer
} from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../../assets/images/logo.png';
// import { useTheme } from '../../../app/providers/ThemeContext'; // 暂时注释掉深色模式
import {
  HomeOutlined,
  MessageOutlined,
  HistoryOutlined,
  CreditCardOutlined,
  BulbOutlined,
  GlobalOutlined,
  // MoonOutlined,  // 暂时注释掉深色模式相关图标
  // SunOutlined,   // 暂时注释掉深色模式相关图标
  MenuOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import UserAvatar from '../../../components/UserAvatar';
import { useAuth } from '../../../contexts/AuthContext';

// 移除全局样式，恢复正常滚动

const { Header, Content, Footer } = Layout;

// 现代化样式组件
const StyledLayout = styled(Layout)`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
`;

const StyledHeader = styled(Header)`
  &.ant-layout-header {
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    padding: 0 24px;
    height: 64px;
    display: flex;
    align-items: center;  // 确保垂直居中
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 1000;

    @media (max-width: 768px) {
      height: 56px;
      padding: 0 16px;
    }
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;  // 水平居中
  cursor: pointer;
  user-select: none;
  transition: all 0.3s ease;
  height: 100%;
  position: relative;  // 添加相对定位
  
  img {
    height: 60px;
    width: 60px;
    object-fit: cover;
    object-position: center;
    transition: transform 0.3s ease;
    transform: scale(1.4);
    position: relative;  // 使用相对定位
    top: 4px;  // 微调垂直位置
    
    @media (max-width: 768px) {
      height: 48px;
      width: 48px;
      transform: scale(1.3);
      top: 2px;  // 移动端稍微调整
    }

    &:hover {
      transform: scale(1.5);
    }
  }
`;

const NavigationMenu = styled(Menu)`
  &.ant-menu {
    background: transparent;
    border: none;
    line-height: 64px;
    font-size: 17px;  // 增大字体尺寸
    
    @media (max-width: 768px) {
      line-height: 56px;
    }
    
    .ant-menu-item {
      margin: 0 8px;  // 增加间距
      padding: 0 20px;  // 增加内边距
      border-radius: 6px;
      transition: all 0.3s ease;
      font-weight: 500;  // 添加字重
      
      &:hover {
        background: rgba(79, 70, 229, 0.06);
        color: #4F46E5;
        transform: translateY(-1px);  // 添加轻微的悬浮效果
      }
      
      &.ant-menu-item-selected {
        background: rgba(79, 70, 229, 0.1);
        color: #4F46E5;
        font-weight: 600;  // 选中时字重略大
        
        &::after {
          display: none;
        }
      }
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
    
    .desktop-only {
      display: none;
    }
  }
`;

const ActionButton = styled(Button)`
  &.ant-btn {
    border: none;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(102, 126, 234, 0.1);
      color: #667eea;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
    }
    
    .anticon {
      font-size: 16px;
    }

    &.desktop-only {
      @media (max-width: 768px) {
        display: none;
      }
    }
  }
`;

const MobileMenuButton = styled(ActionButton)`
  @media (min-width: 769px) {
    display: none;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  }
`;

// 移动端菜单样式也相应调整
const MobileMenu = styled(Menu)`
  &.ant-menu {
    background: transparent;
    border: none;
    font-size: 17px;  // 保持与桌面端一致
    
    .ant-menu-item {
      margin: 4px 16px;
      padding: 0 20px;
      border-radius: 6px;
      font-weight: 500;
      height: 48px;
      line-height: 48px;
      
      &:hover {
        background: rgba(79, 70, 229, 0.06);
        color: #4F46E5;
      }
      
      &.ant-menu-item-selected {
        background: rgba(79, 70, 229, 0.1);
        color: #4F46E5;
        font-weight: 600;
        
        &::after {
          display: none;
        }
      }
    }
  }
`;

const StyledContent = styled(Content)`
  &.ant-layout-content {
    min-height: calc(100vh - 140px);
    padding: 0;
  }
`;

const StyledFooter = styled(Footer)`
  &.ant-layout-footer {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    text-align: center;
    color: #64748b;
    padding: 20px;
  }
`;

const UserSection = styled.div`
  padding: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 12px;

  .user-info {
    flex: 1;
    overflow: hidden;

    .user-name {
      font-size: 16px;
      font-weight: 500;
      color: #1f2937;
      margin-bottom: 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .user-status {
      font-size: 14px;
      color: #6b7280;
    }
  }
`;

interface LayoutComponentProps {
  children: React.ReactNode;
}

const LayoutComponent: React.FC<LayoutComponentProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { state: authState } = useAuth();
  // const { isDarkMode, toggleTheme } = useTheme(); // 暂时注释掉深色模式
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: t('navigation.home'),
    },
    {
      key: '/chat',
      icon: <MessageOutlined />,
      label: t('navigation.chat'),
    },
    {
      key: '/history',
      icon: <HistoryOutlined />,
      label: t('navigation.history'),
    },
    {
      key: '/pricing',
      icon: <CreditCardOutlined />,
      label: t('navigation.pricing'),
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    setMobileMenuVisible(false);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'zh' ? 'ja' : 'zh';
    i18n.changeLanguage(newLang);
  };

  const getCurrentLanguageIcon = () => {
    return i18n.language === 'zh' ? '中' : '日';
  };

  return (
    <StyledLayout>
      <StyledHeader>
        <Logo onClick={() => navigate('/')}>
          <img src={logo} alt="AtoAI Logo" />
        </Logo>
        
        <NavigationMenu
          mode="horizontal"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
        
        <ActionButtons>
          <ActionButton 
            onClick={toggleLanguage}
            icon={<GlobalOutlined />}
            className="desktop-only"
          >
            {getCurrentLanguageIcon()}
          </ActionButton>
          
          <div className="desktop-only">
            <UserAvatar />
          </div>
          
          <MobileMenuButton
            onClick={() => setMobileMenuVisible(true)}
            icon={<MenuOutlined />}
          />
        </ActionButtons>

      </StyledHeader>

      <StyledDrawer
        title={t('navigation.menu')}
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
      >
        <MobileMenu
          mode="vertical"
          selectedKeys={[location.pathname]}
          items={[
            ...menuItems,
            {
              key: 'language',
              icon: <GlobalOutlined />,
              label: i18n.language === 'zh' ? '切换到日文' : '中文に切り替え',
              onClick: () => {
                toggleLanguage();
                setMobileMenuVisible(false);
              }
            }
          ]}
          onClick={handleMenuClick}
        />
        <UserSection>
          <UserAvatar onNavigate={() => setMobileMenuVisible(false)} />
          <div className="user-info">
            <div className="user-name">
              {authState.user?.display_name || t('auth.anonymousMode')}
            </div>
            <div className="user-status">
              {authState.isAuthenticated ? t('common.premium') : t('common.free')}
            </div>
          </div>
        </UserSection>
      </StyledDrawer>

      <StyledContent>
        {children}
      </StyledContent>

      <StyledFooter>
        {t('common.footer')}
      </StyledFooter>
    </StyledLayout>
  );
};

export default LayoutComponent; 